import { useMutation, useQuery, useQueryClient } from "react-query";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import RolesRequest from "../../requests/vendor/RolesRequest";
import {
  setLoading,
  setCreating,
  setUpdating,
  setDeleting,
  setError,
  setCreateError,
  setUpdateError,
  setDeleteError,
  setCustomRoles,
  addCustomRole,
  updateCustomRole,
  removeCustomRole,
  setSelectedRole,
  setIsEditingRole,
  setRoleFormData,
} from "../../redux/RolesSlice";
import { usePermissionCheck } from './usePermissionCheck';
import { useSystemPermissions } from './useSystemPermissions';

export const useCustomRoles = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { canViewRoles } = usePermissionCheck();
  const { getPermissionSlugs, rawPermissions } = useSystemPermissions();

  const {
    customRoles,
    systemPermissions,
    isLoading,
    isCreating,
    isUpdating,
    isDeleting,
    error,
    createError,
    updateError,
    deleteError,
    selectedRole,
    isEditingRole,
    roleFormData,
  } = useSelector((state) => state.roles);

  //* Helper function to transform API role response for frontend use
  const transformApiRoleForFrontend = (apiRole) => {
    if (!apiRole || !apiRole.permissions) return apiRole;

    let groupedPermissions = {};

    // Check if permissions is already in grouped format (object with modules as keys)
    if (typeof apiRole.permissions === 'object' && !Array.isArray(apiRole.permissions)) {
      // Permissions are already grouped by module (like in create/update responses)
      groupedPermissions = apiRole.permissions;
    } else if (Array.isArray(apiRole.permissions)) {
      // Permissions are in array format (like in initial fetch) - group them by module
      apiRole.permissions.forEach(permission => {
        const module = permission.module;
        if (!groupedPermissions[module]) {
          groupedPermissions[module] = [];
        }
        groupedPermissions[module].push(permission.name);
      });
    }

    return {
      ...apiRole,
      permissions: groupedPermissions,
      originalPermissions: apiRole.permissions
    };
  };

  //* Helper function to transform permissions for API calls
  const transformPermissionsForApi = (permissions) => {
    if (!rawPermissions) {
      // Fallback to simple array format if raw permissions not available
      return [];
    }

    const slugs = [];
    Object.keys(permissions).forEach(module => {
      const actionNames = permissions[module] || [];
      const modulePermissions = rawPermissions[module] || [];
      
      actionNames.forEach(actionName => {
        const permission = modulePermissions.find(perm => perm.name === actionName);
        if (permission && permission.permission_slug) {
          slugs.push(permission.permission_slug);
        }
      });
    });
    
    return slugs;
  };

  // Fetch custom roles
  const {
    data: rolesData,
    isLoading: fetchLoading,
    error: fetchError,
    refetch: refetchRoles,
  } = useQuery("customRoles", RolesRequest.getCustomRoles, {
    staleTime: 2 * 60 * 1000,
    enabled: canViewRoles(),
    onSuccess: (data) => {
      if (data.success) {
        const transformedRoles = data.data.map(transformApiRoleForFrontend);
        dispatch(setCustomRoles(transformedRoles));
      } else {
        dispatch(setError(data.message));
      }
    },
    onError: (error) => {
      dispatch(setError(error.message));
    },
  });

  //* Create custom role mutation
  const createRoleMutation = useMutation(
    (roleData) => {
      // Transform permissions to use slugs for API call
      const permissionSlugs = transformPermissionsForApi(roleData.permissions);
      return RolesRequest.createCustomRole({
        name: roleData.name,
        permissions: permissionSlugs
      });
    },
    {
      onMutate: () => {
        dispatch(setCreating(true));
      },
      onSuccess: (data) => {
        if (data.success) {
          // Transform the created role for frontend use
          const transformedRole = transformApiRoleForFrontend(data.data);
          dispatch(addCustomRole(transformedRole));
          queryClient.invalidateQueries("customRoles");
          toast.success("Custom role created successfully!");
        } else {
          dispatch(setCreateError(data.message));
          toast.error(data.message);
        }
      },
      onError: (error) => {
        dispatch(setCreateError(error.message));
        toast.error("Failed to create custom role");
      },
      onSettled: () => {
        dispatch(setCreating(false));
      },
    }
  );

  //* Update custom role mutation
  const updateRoleMutation = useMutation(
    ({ roleSlug, roleData }) => {
      const permissionSlugs = transformPermissionsForApi(roleData.permissions);
      return RolesRequest.updateCustomRole(roleSlug, {
        name: roleData.name,
        permissions: permissionSlugs
      });
    },
    {
      onMutate: () => {
        dispatch(setUpdating(true));
      },
      onSuccess: (data, variables) => {
        console.log('Update success - Raw response:', data);
        console.log('Variables:', variables);
        
        if (data.success) {
          console.log('Success is true, transforming role...');
          // Transform the updated role for frontend use
          const transformedRole = transformApiRoleForFrontend(data.data);
          console.log('Transformed role:', transformedRole);
          
          dispatch(updateCustomRole({ role_slug: variables.roleSlug, ...transformedRole }));
          queryClient.invalidateQueries("customRoles");
          queryClient.invalidateQueries("teamMembers");
          toast.success("Custom role updated successfully!");
        } else {
          console.log('Success is false, showing error:', data.message);
          dispatch(setUpdateError(data.message));
          toast.error(data.message);
        }
      },
      onError: (error) => {
        console.log('Update error:', error);
        dispatch(setUpdateError(error.message));
        toast.error("Failed to update custom role");
      },
      onSettled: () => {
        dispatch(setUpdating(false));
      },
    }
  );

  //* Delete custom role mutation
  const deleteRoleMutation = useMutation(RolesRequest.deleteCustomRole, {
    onMutate: () => {
      dispatch(setDeleting(true));
    },
    onSuccess: (data, roleSlug) => {
      if (data.success) {
        dispatch(removeCustomRole(roleSlug));
        queryClient.invalidateQueries("customRoles");
        toast.success("Custom role deleted successfully!");
      } else {
        dispatch(setDeleteError(data.message));
        toast.error(data.message);
      }
    },
    onError: (error) => {
      dispatch(setDeleteError(error.message));
      toast.error("Failed to delete custom role");
    },
    onSettled: () => {
      dispatch(setDeleting(false));
    },
  });

  //* Check if role can be deleted
  const checkRoleDeletableMutation = useMutation(
    RolesRequest.checkRoleDeletable,
    {
      onSuccess: (data) => {
        return data;
      },
    }
  );

  return {
    //* Data
    customRoles,
    systemPermissions,
    selectedRole,
    isEditingRole,
    roleFormData,

    //* Loading states
    isLoading: isLoading || fetchLoading,
    isCreating,
    isUpdating,
    isDeleting,

    //* Errors
    error: error || fetchError?.message,
    createError,
    updateError,
    deleteError,

    //* Actions
    createRole: createRoleMutation.mutate,
    updateRole: updateRoleMutation.mutate,
    deleteRole: deleteRoleMutation.mutate,
    checkRoleDeletable: checkRoleDeletableMutation.mutateAsync,
    refetchRoles,

    //* UI state management
    setSelectedRole: (role) => dispatch(setSelectedRole(role)),
    setIsEditingRole: (editing) => dispatch(setIsEditingRole(editing)),
    setRoleFormData: (data) => dispatch(setRoleFormData(data)),

    //* Helper functions
    canCreateRoles: canViewRoles,
    hasRoles: customRoles.length > 0,
    getRoleById: (id) => customRoles.find((role) => role.role_slug === id),
    getRoleByName: (name) => customRoles.find((role) => role.name === name),
  };
};
